import { useEffect, useRef, useState } from "react";
import { Check, Plus, Search, User } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAdaptiveDebounce } from "@/lib/debounce";
import { titleCasePtBR } from "@/lib/name-case";

type Value = {
  id: string | null;
  name: string;
  phone: string;
};

type Props = {
  value: Value;
  onChange: (v: Value) => void;
  allowWalkIn?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  /** Quando informado, "Paciente sem cadastro" abre o cadastro completo em vez de
   *  só guardar o nome digitado — chamado com o nome já digitado. */
  onCreateNew?: (name: string) => void;
};

/** Combobox de paciente: busca existentes + opção walk-in (texto livre). */
export function PatientCombobox({
  value,
  onChange,
  allowWalkIn = true,
  autoFocus,
  placeholder = "Digite o nome do paciente...",
  onCreateNew,
}: Props) {
  const [search, setSearch] = useState(value.name);
  const [open, setOpen] = useState(false);
  const debouncedSearch = useAdaptiveDebounce(search);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sem filtro de `active` — paciente inativo também precisa aparecer aqui
  // (ele existe, só está com o cadastro marcado como inativo). Mostramos um
  // badge "Inativo" no resultado em vez de escondê-lo.
  const query = trpc.patient.list.useQuery(
    { search: debouncedSearch, take: 20 },
    { enabled: open && debouncedSearch.trim().length > 0 },
  );

  useEffect(() => {
    if (value.name !== search && !open) setSearch(value.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.name]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const patients = (query.data?.patients ?? []) as Array<{
    id: string;
    name: string;
    phone: string | null;
    active: boolean;
  }>;

  const hasExactMatch = patients.some(
    (p) => p.name.toLowerCase() === debouncedSearch.toLowerCase(),
  );
  const showWalkInOption =
    allowWalkIn && debouncedSearch.trim().length >= 2 && !hasExactMatch;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
        <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        <input
          type="text"
          value={search}
          autoFocus={autoFocus}
          autoCapitalize="words"
          autoComplete="name"
          onChange={(e) => {
            // Formatação inteligente (Title Case PT-BR) — mesmo padrão do
            // PatientForm. Preserva espaços exatos do input pra não
            // quebrar cursor durante digitação.
            const raw = e.target.value;
            const formatted = titleCasePtBR(raw);
            setSearch(formatted);
            setOpen(true);
            if (value.id) {
              onChange({ id: null, name: formatted, phone: "" });
            } else {
              onChange({ ...value, name: formatted });
            }
          }}
          onBlur={(e) => {
            // Colapsa múltiplos espaços + trim quando sai do campo
            const cleaned = e.target.value.replace(/\s+/g, " ").trim();
            if (cleaned !== search) {
              setSearch(cleaned);
              if (!value.id) onChange({ ...value, name: cleaned });
            }
          }}
          onClick={() => {
            if (search.trim().length > 0) setOpen(true);
          }}
          placeholder={placeholder}
          className="h-9 w-full border-0 bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 focus:ring-0 sm:text-sm"
        />
        {/* Só o ícone: a tag escrita ficava dentro da linha do campo de busca e comia largura útil
            do input em 390px. O rótulo continua existindo pra leitor de tela e no `title`. */}
        {value.id && (
          <span
            title="Paciente cadastrado"
            aria-label="Paciente cadastrado"
            role="img"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:ring-emerald-500/30"
          >
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
        )}
      </div>

      {open && (query.isFetching || patients.length > 0 || showWalkInOption) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[280px] overflow-auto rounded-md border border-zinc-200 bg-white shadow-xl">
          {query.isFetching && (
            <div className="px-3 py-2 text-[11px] text-zinc-400">Buscando...</div>
          )}
          {!query.isFetching && patients.length === 0 && debouncedSearch && !showWalkInOption && (
            <div className="px-3 py-2 text-[11px] text-zinc-400">Nenhum paciente encontrado</div>
          )}
          {patients.map((p) => (
            <button
              key={p.id}
              type="button"
              // Seleção no pointerdown com preventDefault: no iOS o toque fecha a lista e o clique
              // sintetizado (~300ms depois) caía no que aparecesse no MESMO lugar — o "Ver perfil"
              // do diálogo, navegando sem querer. Prevenir o pointerdown suprime esse clique.
              // onClick fica de fallback pro teclado (Enter), com guarda de idempotência no open.
              onPointerDown={(e) => {
                e.preventDefault();
                onChange({ id: p.id, name: p.name, phone: p.phone ?? "" });
                setSearch(p.name);
                setOpen(false);
              }}
              onClick={() => {
                if (!open) return;
                onChange({ id: p.id, name: p.name, phone: p.phone ?? "" });
                setSearch(p.name);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-brand-50"
            >
              <User className="h-3.5 w-3.5 text-zinc-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-medium text-zinc-900">{p.name}</p>
                  {!p.active && (
                    <span className="shrink-0 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-zinc-500">
                      Inativo
                    </span>
                  )}
                </div>
                {p.phone && (
                  <p className="truncate text-[10px] text-zinc-500">{p.phone}</p>
                )}
              </div>
            </button>
          ))}
          {showWalkInOption && (
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                if (onCreateNew) {
                  onCreateNew(debouncedSearch);
                  setOpen(false);
                  return;
                }
                onChange({ id: null, name: debouncedSearch, phone: value.phone });
                setSearch(debouncedSearch);
                setOpen(false);
              }}
              onClick={() => {
                if (!open) return;
                if (onCreateNew) {
                  onCreateNew(debouncedSearch);
                  setOpen(false);
                  return;
                }
                onChange({ id: null, name: debouncedSearch, phone: value.phone });
                setSearch(debouncedSearch);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-brand-700 hover:bg-brand-50"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="text-sm font-medium">
                {onCreateNew ? "Cadastrar novo paciente: " : "Paciente sem cadastro: "}
                <span className="font-semibold">"{debouncedSearch}"</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
